// src/pages/performance/ManageAppraisalsAppraisalDialogs.jsx
import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";


import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import GroupAddIcon from "@mui/icons-material/GroupAdd";
import TuneIcon from "@mui/icons-material/Tune";

import { API, CYCLE_OPTIONS, CURRENT_YEAR, PERIOD_OPTIONS, WORKFLOW_TEMPLATE_OPTIONS_FALLBACK } from "./manageAppraisals.constants";
import { safeMsg, normalizeSingleResponse } from "./manageAppraisals.utils";

/* ---------------------------------------
   Create Appraisal Dialog
---------------------------------------- */
export function CreateAppraisalDialog({
  open,
  onClose,
  onCreated,
  onError,
  workflowTemplateOptions = WORKFLOW_TEMPLATE_OPTIONS_FALLBACK,
}) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const [employeeCode, setEmployeeCode] = useState("");

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [workflowTemplateSlug, setWorkflowTemplateSlug] = useState("");
  const [overrideSupervisorId, setOverrideSupervisorId] = useState("");
  const [overrideSecondLevelId, setOverrideSecondLevelId] = useState("");

  const [period, setPeriod] = useState("");
  const [cycle, setCycle] = useState("");
  const [label, setLabel] = useState("");
  const [attributesJson, setAttributesJson] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setEmployeeCode("");
    setShowAdvanced(false);
    setWorkflowTemplateSlug("");
    setOverrideSupervisorId("");
    setOverrideSecondLevelId("");
    setPeriod("");
    setCycle("");
    setLabel("");
    setAttributesJson("");
  }, [open]);

  const handleSubmit = async () => {
    onError?.("");

    if (!employeeCode.trim()) {
      onError?.("Employee code is required");
      return;
    }

    let attributes = {};
    if (period) attributes.period = period;
    if (cycle) attributes.cycle = cycle;
    if (label.trim()) attributes.label = label.trim();

    if (attributesJson.trim()) {
      try {
        const parsed = JSON.parse(attributesJson);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
        attributes = { ...attributes, ...parsed };
      } catch {
        onError?.('Invalid attributes JSON. Use an object like {"period":"2025","cycle":"annual"}');
        return;
      }
    }

    const payload = {
      employee_code: employeeCode.trim(),
      workflow_template_slug: workflowTemplateSlug || null,
      override_supervisor_id: overrideSupervisorId ? Number(overrideSupervisorId) : null,
      override_second_level_id: overrideSecondLevelId ? Number(overrideSecondLevelId) : null,
    };

    if (Object.keys(attributes).length > 0) payload.attributes = attributes;
    Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);

    setSubmitting(true);
    try {
      const { data } = await api.post(API.appraisals, payload);
      onCreated?.(data?.message);
      onClose?.();
    } catch (err) {
      onError?.(safeMsg(err, "Failed to create appraisal"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Create New Appraisal</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            }}
          >
            Enter the employee code. Use advanced options to override workflow and store attributes (period/cycle).
          </Alert>

          <TextField
            label="Employee Code"
            value={employeeCode}
            onChange={(e) => setEmployeeCode(e.target.value)}
            required
            fullWidth
            placeholder="e.g. 10485"
          />

          <Button
            variant="text"
            startIcon={<TuneIcon />}
            onClick={() => setShowAdvanced((s) => !s)}
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}
          >
            {showAdvanced ? "Hide advanced options" : "Show advanced options"}
          </Button>

          <Collapse in={showAdvanced}>
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Workflow Template"
                    value={workflowTemplateSlug}
                    onChange={(e) => setWorkflowTemplateSlug(e.target.value)}
                    fullWidth
                    helperText="Optional: override the default workflow picked by the system."
                  >
                    {workflowTemplateOptions.map((opt) => (
                      <MenuItem key={opt.value || "auto"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Override Supervisor User ID"
                    value={overrideSupervisorId}
                    onChange={(e) => setOverrideSupervisorId(e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                    helperText="Optional override if appraiser is different from default supervisor."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Override Second Level User ID"
                    value={overrideSecondLevelId}
                    onChange={(e) => setOverrideSecondLevelId(e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                    helperText="Optional second-level override."
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 900, opacity: 0.85 }}>
                  Appraisal attributes (optional)
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Period / Year" value={period} onChange={(e) => setPeriod(e.target.value)} fullWidth>
                      <MenuItem value="">None</MenuItem>
                      {PERIOD_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField select label="Cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} fullWidth>
                      <MenuItem value="">None</MenuItem>
                      {CYCLE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Label / Name"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      fullWidth
                      placeholder="e.g. Annual 2025"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Raw Attributes JSON (advanced)"
                      value={attributesJson}
                      onChange={(e) => setAttributesJson(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder='{"custom_key":"value"}'
                      helperText="Optional advanced override. If provided, it will be merged with the fields above."
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------
   Bulk Create Appraisals Dialog
---------------------------------------- */
export function BulkCreateAppraisalsDialog({
  open,
  onClose,
  onDone,
  onError,
  workflowTemplateOptions = WORKFLOW_TEMPLATE_OPTIONS_FALLBACK,
}) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const [period, setPeriod] = useState(String(CURRENT_YEAR));
  const [cycle, setCycle] = useState("annual");
  const [label, setLabel] = useState(`Annual ${CURRENT_YEAR}`);
  const [workflowTemplateSlug, setWorkflowTemplateSlug] = useState("");

  const [skipExisting, setSkipExisting] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setPeriod(String(CURRENT_YEAR));
    setCycle("annual");
    setLabel(`Annual ${CURRENT_YEAR}`);
    setWorkflowTemplateSlug("");
    setSkipExisting(true);
  }, [open]);

  const handleSubmit = async () => {
    onError?.("");

    if (!period) {
      onError?.("Please select the year (period).");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        period,
        cycle: cycle || null,
        label: label?.trim() || null,
        workflow_template_slug: workflowTemplateSlug || null,
        skip_existing: !!skipExisting,
      };

      const { data } = await api.post(API.bulkCreate, payload);

      const created = data?.created_count ?? data?.created ?? data?.count_created ?? data?.data?.created_count ?? null;
      const skipped = data?.skipped_count ?? data?.skipped ?? data?.count_skipped ?? data?.data?.skipped_count ?? null;

      if (created != null || skipped != null) {
        onDone?.(`Bulk create finished. Created: ${created ?? "—"} / Skipped: ${skipped ?? "—"}`);
      } else {
        onDone?.(data?.message || "Bulk create finished.");
      }

      onClose?.();
    } catch (err) {
      onError?.(
        safeMsg(err, "Bulk create failed. Make sure you implemented POST /performance/appraisals/bulk-create in backend.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Bulk Create Appraisals</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            }}
          >
            This will create an appraisal for <b>all employees</b> for the selected year.
            <br />
            Recommended: keep workflow template on <b>Auto-select</b>.
          </Alert>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Period / Year" value={period} onChange={(e) => setPeriod(e.target.value)} fullWidth>
                {PERIOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select label="Cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} fullWidth>
                {CYCLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Label (optional)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
                placeholder="e.g. Annual 2025"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Workflow Template"
                value={workflowTemplateSlug}
                onChange={(e) => setWorkflowTemplateSlug(e.target.value)}
                fullWidth
                helperText="Optional override. Leave empty for auto-select based on role."
              >
                {workflowTemplateOptions.map((opt) => (
                  <MenuItem key={opt.value || "auto"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} />}
                label="Skip employees who already have an appraisal for this period/cycle"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={<GroupAddIcon />}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Creating..." : "Create for All"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------
   Edit Appraisal Dialog
---------------------------------------- */
export function EditAppraisalDialog({
  open,
  appraisalId,
  onClose,
  onSaved,
  onError,
  workflowTemplateOptions = WORKFLOW_TEMPLATE_OPTIONS_FALLBACK,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [appraisal, setAppraisal] = useState(null);
  const [workflowTemplateSlug, setWorkflowTemplateSlug] = useState("");

  const [period, setPeriod] = useState("");
  const [cycle, setCycle] = useState("");
  const [label, setLabel] = useState("");
  const [attributesJson, setAttributesJson] = useState("");

  const fetchDetails = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    onError?.("");
    try {
      const res = await api.get(`${API.appraisals}/${appraisalId}`);
      const data = normalizeSingleResponse(res);
      setAppraisal(data);

      const slug = data?.workflowTemplate?.slug || data?.workflow_template?.slug || data?.workflow_template_slug || "";
      setWorkflowTemplateSlug(slug || "");

      const attrs = data?.attributes || {};
      setPeriod(attrs?.period ? String(attrs.period) : "");
      setCycle(attrs?.cycle ? String(attrs.cycle) : "");
      setLabel(attrs?.label ? String(attrs.label) : "");
      setAttributesJson("");
    } catch (err) {
      onError?.(safeMsg(err, "Failed to load appraisal for editing"));
    } finally {
      setLoading(false);
    }
  }, [appraisalId, onError]);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setAppraisal(null);
    fetchDetails();
  }, [open, fetchDetails]);

  const handleSave = async () => {
    if (!appraisalId) return;
    onError?.("");

    let attributes = {};
    if (period) attributes.period = period;
    if (cycle) attributes.cycle = cycle;
    if (label.trim()) attributes.label = label.trim();

    if (attributesJson.trim()) {
      try {
        const parsed = JSON.parse(attributesJson);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
        attributes = { ...attributes, ...parsed };
      } catch {
        onError?.('Invalid attributes JSON. Use an object like {"period":"2025"}');
        return;
      }
    }

    const payload = { workflow_template_slug: workflowTemplateSlug || null };
    if (Object.keys(attributes).length > 0) payload.attributes = attributes;

    Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);

    setSubmitting(true);
    try {
      const { data } = await api.put(`${API.appraisals}/${appraisalId}`, payload);
      onSaved?.(data?.message || "Appraisal updated successfully");
      onClose?.();
    } catch (err) {
      onError?.(safeMsg(err, "Failed to update appraisal"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Edit Appraisal</DialogTitle>

      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {!loading && !appraisal && <Alert severity="info">No appraisal loaded.</Alert>}

        {!!appraisal && (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.06) }}>
              This edits the appraisal template + attributes.
              <br />
              To change step assignees, use <b>Customize Workflow</b> from the details drawer.
            </Alert>

            <TextField
              label="Employee"
              value={`${appraisal?.employee?.name || "—"} (${appraisal?.employee?.employee_code || "—"})`}
              fullWidth
              disabled
            />

            <TextField
              select
              label="Workflow Template"
              value={workflowTemplateSlug}
              onChange={(e) => setWorkflowTemplateSlug(e.target.value)}
              fullWidth
              helperText="Optional override. Leave empty for auto-select."
            >
              {workflowTemplateOptions.map((opt) => (
                <MenuItem key={opt.value || "auto"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Attributes (optional)
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField select label="Period / Year" value={period} onChange={(e) => setPeriod(e.target.value)} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {PERIOD_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField select label="Cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {CYCLE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} fullWidth />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Raw Attributes JSON (advanced)"
                  value={attributesJson}
                  onChange={(e) => setAttributesJson(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder='{"custom_key":"value"}'
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || loading || !appraisalId}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------
   Delete Appraisal Dialog
---------------------------------------- */
export function DeleteAppraisalDialog({ open, appraisalId, onClose, onDeleted, onError }) {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSubmitting(false);
  }, [open]);

  const handleDelete = async () => {
    if (!appraisalId) return;
    onError?.("");

    setSubmitting(true);
    try {
      const { data } = await api.delete(`${API.appraisals}/${appraisalId}`);
      onDeleted?.(data?.message || "Appraisal deleted successfully");
      onClose?.();
    } catch (err) {
      onError?.(safeMsg(err, "Failed to delete appraisal"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 900 }}>Delete Appraisal</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          This will permanently delete appraisal <strong>#{appraisalId}</strong> including step instances/responses.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.8 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={submitting}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.2, fontWeight: 900, boxShadow: "none" }}
        >
          {submitting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
